import React, { useCallback, useEffect, useRef, useState } from "react";
import { handleTokenAndPostRequest } from "../utils/utils";
import AIAgentSettingsModal from "../components/AIAgentSettingsModal";
import {
  FaArrowUp, FaSpinner, FaExternalLinkAlt,
  FaRegNewspaper, FaBullhorn, FaHome, FaCog,
  FaPlus, FaTrash, FaChevronDown,
} from "react-icons/fa";

/**
 * AI Assistant chat page.
 *
 * Streams the assistant's reply from /ai-assistant-chat (SSE). Tool calls
 * surface as a small inline "thinking" row with a spinner; once the model
 * starts producing the final answer it streams in token-by-token.
 */

const SUGGESTIONS = [
  { icon: FaRegNewspaper, label: "How many contacts do I have?" },
  { icon: FaRegNewspaper, label: "Write an article about the Surry Hills market" },
  { icon: FaBullhorn, label: "Make a square infographic for Bondi sales trends" },
];

const TOOL_LABELS = {
  query_my_data: "Looking up your data",
  generate_article: "Drafting article",
  generate_infographic: "Designing infographic",
  find_property: "Searching properties",
  create_appraisal: "Creating appraisal",
};

function AssetCard({ asset }) {
  const Icon =
    asset.kind === "article" ? FaRegNewspaper :
    asset.kind === "infographic" ? FaBullhorn :
    asset.kind === "property" ? FaHome :
    asset.kind === "appraisal" ? FaHome : FaExternalLinkAlt;

  const label =
    asset.kind === "article" ? "Open article" :
    asset.kind === "infographic" ? "Open in platform" :
    asset.kind === "property" ? "Open property" :
    asset.kind === "appraisal" ? "Open appraisal" : "Open link";

  const href = asset.public_url || asset.platform_url;
  if (!href) return null;
  const isExternal = !!asset.public_url;

  return (
    <a
      href={href}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer" : undefined}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "10px 14px", marginTop: 8,
        background: "#f3f4f6", border: "1px solid #e5e7eb",
        borderRadius: 12, textDecoration: "none", color: "#111827",
        maxWidth: 420,
      }}
    >
      <div style={{
        width: 32, height: 32, borderRadius: 8,
        background: "#fff", border: "1px solid #e5e7eb",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "#0f3c3f", flexShrink: 0,
      }}>
        <Icon style={{ fontSize: 14 }} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{
          fontSize: 13, fontWeight: 600,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{asset.title || "Generated asset"}</div>
        <div style={{ fontSize: 11, color: "#6b7280" }}>{label}</div>
      </div>
      <FaExternalLinkAlt style={{ fontSize: 11, color: "#6b7280" }} />
    </a>
  );
}

function PersonaAvatar({ name, photo, size = 32 }) {
  const [errored, setErrored] = useState(false);
  const initial = (name || "A").trim().charAt(0).toUpperCase();
  if (photo && !errored) {
    return (
      <img
        src={photo}
        alt={name || "AI assistant"}
        style={{
          width: size, height: size, borderRadius: "50%",
          objectFit: "cover", flexShrink: 0,
          border: "1px solid #e5e7eb",
        }}
        onError={() => setErrored(true)}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: "linear-gradient(135deg, #10b981, #0ea5e9)",
      color: "#fff", fontWeight: 700, fontSize: size * 0.45,
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0,
    }}>{initial}</div>
  );
}

function ThinkingRow({ label, done }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 8,
      padding: "6px 12px", margin: "4px 0",
      background: "#f3f4f6", border: "1px solid #e5e7eb",
      borderRadius: 999, color: "#6b7280", fontSize: 13,
    }}>
      {done ? (
        <span style={{
          width: 11, height: 11, borderRadius: "50%",
          background: "#10b981", display: "inline-block",
        }} />
      ) : (
        <FaSpinner style={{ animation: "ai-spin 1s linear infinite", fontSize: 11 }} />
      )}
      <span>{label}</span>
    </div>
  );
}

function MessageBubble({ msg, persona }) {
  if (msg.role === "user") {
    return (
      <div style={{ display: "flex", justifyContent: "flex-end", margin: "20px 0" }}>
        <div style={{
          background: "#ececec", color: "#111827",
          padding: "12px 16px", borderRadius: 22,
          maxWidth: "75%", fontSize: 15, lineHeight: 1.5,
          whiteSpace: "pre-wrap", wordBreak: "break-word",
        }}>{msg.content}</div>
      </div>
    );
  }

  // assistant
  return (
    <div style={{ display: "flex", gap: 12, margin: "20px 0", alignItems: "flex-start" }}>
      <PersonaAvatar name={persona?.name} photo={persona?.photo} size={32} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {Array.isArray(msg.thinking) && msg.thinking.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            {msg.thinking.map((t, i) => (
              <ThinkingRow
                key={i}
                done={t.done}
                label={TOOL_LABELS[t.name] || t.name}
              />
            ))}
          </div>
        )}
        {(msg.content || msg.streaming) && (
          <div style={{
            fontSize: 15, lineHeight: 1.55, color: "#111827",
            whiteSpace: "pre-wrap", wordBreak: "break-word",
            marginTop: msg.thinking && msg.thinking.length > 0 ? 8 : 0,
          }}>
            {msg.content}
            {msg.streaming && (
              <span style={{
                display: "inline-block", width: 8, height: 16,
                background: "#111827", marginLeft: 2, verticalAlign: "text-bottom",
                animation: "ai-blink 1s steps(2, start) infinite",
              }} />
            )}
          </div>
        )}
        {Array.isArray(msg.assets) && msg.assets.length > 0 && (
          <div>{msg.assets.map((a, i) => <AssetCard key={i} asset={a} />)}</div>
        )}
      </div>
    </div>
  );
}

function HeaderSkeleton({ isMobile }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: isMobile ? "12px 16px" : "16px 32px",
      borderBottom: "1px solid #e5e7eb", flexShrink: 0,
    }}>
      <div style={{
        width: isMobile ? 32 : 36, height: isMobile ? 32 : 36,
        borderRadius: "50%", background: "#e5e7eb",
        animation: "ai-skel 1.4s ease-in-out infinite",
      }} />
      <div style={{ flex: 1 }}>
        <div style={{
          width: 120, height: 14, borderRadius: 6, background: "#e5e7eb",
          marginBottom: 6, animation: "ai-skel 1.4s ease-in-out infinite",
        }} />
        <div style={{
          width: 80, height: 10, borderRadius: 6, background: "#eef2f5",
          animation: "ai-skel 1.4s ease-in-out infinite",
        }} />
      </div>
      <div style={{
        width: 96, height: 28, borderRadius: 999, background: "#eef2f5",
        animation: "ai-skel 1.4s ease-in-out infinite",
      }} />
    </div>
  );
}

function BodySkeleton({ isMobile }) {
  return (
    <div style={{
      flex: 1, padding: isMobile ? "40px 16px" : "60px 32px",
      display: "flex", justifyContent: "center",
    }}>
      <div style={{ maxWidth: 480, width: "100%", textAlign: "center" }}>
        <div style={{
          width: 64, height: 64, borderRadius: "50%",
          background: "#e5e7eb", margin: "0 auto 20px",
          animation: "ai-skel 1.4s ease-in-out infinite",
        }} />
        <div style={{
          width: "60%", height: 20, borderRadius: 8,
          background: "#e5e7eb", margin: "0 auto 12px",
          animation: "ai-skel 1.4s ease-in-out infinite",
        }} />
        <div style={{
          width: "85%", height: 12, borderRadius: 6,
          background: "#eef2f5", margin: "0 auto 8px",
          animation: "ai-skel 1.4s ease-in-out infinite",
        }} />
        <div style={{
          width: "70%", height: 12, borderRadius: 6,
          background: "#eef2f5", margin: "0 auto 32px",
          animation: "ai-skel 1.4s ease-in-out infinite",
        }} />
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>
          {[140, 180, 160, 130].map((w, i) => (
            <div key={i} style={{
              width: w, height: 32, borderRadius: 999, background: "#eef2f5",
              animation: "ai-skel 1.4s ease-in-out infinite",
            }} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AIAssistant() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [persona, setPersona] = useState({ name: "", photo: "", title: "" });
  const [personaLoading, setPersonaLoading] = useState(true);
  const [messages, setMessages] = useState([]);   // {role, content, assets?, thinking?, streaming?}
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [chats, setChats] = useState([]);                 // [{id, title, updated_at}]
  const [activeChatId, setActiveChatId] = useState(null);
  const [chatsLoading, setChatsLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [mobileChatPickerOpen, setMobileChatPickerOpen] = useState(false);
  const [pendingDeleteChatId, setPendingDeleteChatId] = useState(null);
  const [deletingChat, setDeletingChat] = useState(false);
  const [showBetaDisclaimer, setShowBetaDisclaimer] = useState(true);
  const [showAgentSettingsModal, setShowAgentSettingsModal] = useState(false);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Load AI persona
  useEffect(() => {
    handleTokenAndPostRequest(`${window.api_host}/ai-assistant-persona`, {})
      .then((res) => {
        if (res?.data?.status === "success") {
          setPersona({
            name: res.data.name || "Assistant",
            photo: res.data.photo || "",
            title: res.data.title || "",
          });
        } else {
          setPersona({ name: "Assistant", photo: "", title: "" });
        }
      })
      .catch(() => setPersona({ name: "Assistant", photo: "", title: "" }))
      .finally(() => setPersonaLoading(false));
  }, []);

  // Load chat list
  const loadChats = useCallback(() => {
    return handleTokenAndPostRequest(`${window.api_host}/ai-assistant-chats`, {})
      .then((res) => {
        if (res?.data?.status === "success") {
          setChats(Array.isArray(res.data.chats) ? res.data.chats : []);
        }
      })
      .catch(() => {})
      .finally(() => setChatsLoading(false));
  }, []);

  useEffect(() => { loadChats(); }, [loadChats]);

  function startNewChat() {
    setActiveChatId(null);
    setMessages([]);
    setMobileChatPickerOpen(false);
  }

  function openChat(chatId) {
    if (chatId === activeChatId || sending) return;
    setActiveChatId(chatId);
    setMobileChatPickerOpen(false);
    setLoadingMessages(true);
    setMessages([]);
    handleTokenAndPostRequest(
      `${window.api_host}/ai-assistant-chat-messages`,
      { chat_id: chatId },
    )
      .then((res) => {
        if (res?.data?.status === "success") {
          const loaded = (res.data.messages || []).map((m) => ({
            role: m.role,
            content: m.content || "",
            assets: m.assets || [],
            thinking: (m.thinking || []).map((t) => ({ ...t, done: true })),
          }));
          setMessages(loaded);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingMessages(false));
  }

  function deleteChat(chatId, e) {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    setPendingDeleteChatId(chatId);
  }

  async function confirmDeleteChat() {
    const chatId = pendingDeleteChatId;
    if (!chatId) return;
    setDeletingChat(true);
    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/ai-assistant-chat-delete`,
        { chat_id: chatId },
      );
      setChats((c) => c.filter((x) => x.id !== chatId));
      if (activeChatId === chatId) startNewChat();
    } catch (err) { /* ignore */ }
    setDeletingChat(false);
    setPendingDeleteChatId(null);
  }

  // Auto-scroll on updates
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Auto-grow composer textarea (max ~6 rows)
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 160) + "px";
  }, [input]);

  function updateLastAssistant(updater) {
    setMessages((curr) => {
      const next = curr.slice();
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i].role === "assistant") {
          next[i] = updater({ ...next[i] });
          break;
        }
      }
      return next;
    });
  }

  async function send(messageOverride) {
    const text = (messageOverride ?? input).trim();
    if (!text || sending) return;

    const newUserMsg = { role: "user", content: text };
    const history = messages.map(({ role, content }) => ({ role, content }));
    const assistantPlaceholder = {
      role: "assistant", content: "", assets: [], thinking: [], streaming: true,
    };
    setMessages((m) => [...m, newUserMsg, assistantPlaceholder]);
    setInput("");
    setSending(true);

    try {
      const jwt = localStorage.getItem("jwt_token");
      const headers = { "Content-Type": "application/json" };
      if (jwt) headers.Authorization = `Bearer ${jwt}`;

      const resp = await fetch(`${window.api_host}/ai-assistant-chat`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          message: text,
          messages: history,
          stream: true,
          chat_id: activeChatId,
        }),
      });

      if (!resp.ok || !resp.body) {
        updateLastAssistant((m) => ({
          ...m, streaming: false,
          content: m.content || "Sorry — something went wrong.",
        }));
        setSending(false);
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop();

        for (const ev of events) {
          const dataLine = ev.split("\n").find((l) => l.startsWith("data: "));
          if (!dataLine) continue;
          let parsed;
          try { parsed = JSON.parse(dataLine.slice(6)); } catch { continue; }

          if (parsed.type === "text") {
            updateLastAssistant((m) => ({
              ...m, content: (m.content || "") + (parsed.delta || ""),
            }));
          } else if (parsed.type === "chat_meta") {
            // Newly-created chat — capture id + title and prepend to sidebar.
            if (parsed.chat_id) {
              setActiveChatId(parsed.chat_id);
              setChats((prev) => {
                if (prev.some((c) => c.id === parsed.chat_id)) return prev;
                return [{
                  id: parsed.chat_id,
                  title: parsed.title || "New chat",
                  updated_at: new Date().toISOString(),
                }, ...prev];
              });
            }
          } else if (parsed.type === "tool_start") {
            updateLastAssistant((m) => ({
              ...m, thinking: [...(m.thinking || []), { name: parsed.name, done: false }],
            }));
          } else if (parsed.type === "tool_end") {
            updateLastAssistant((m) => {
              const thinking = (m.thinking || []).slice();
              for (let i = thinking.length - 1; i >= 0; i--) {
                if (thinking[i].name === parsed.name && !thinking[i].done) {
                  thinking[i] = { ...thinking[i], done: true, error: parsed.error };
                  break;
                }
              }
              return { ...m, thinking };
            });
          } else if (parsed.type === "asset") {
            updateLastAssistant((m) => ({
              ...m, assets: [...(m.assets || []), parsed.asset],
            }));
          } else if (parsed.type === "done") {
            updateLastAssistant((m) => ({
              ...m, streaming: false,
              content: m.content || parsed.reply || "",
              assets: m.assets && m.assets.length ? m.assets : (parsed.assets || []),
            }));
          } else if (parsed.type === "error") {
            updateLastAssistant((m) => ({
              ...m, streaming: false,
              content: (m.content || "") + `\n\n_${parsed.message || "Error"}_`,
            }));
          }
        }
      }
    } catch (e) {
      updateLastAssistant((m) => ({
        ...m, streaming: false,
        content: m.content || "Sorry — I couldn't reach the assistant. Please try again.",
      }));
    } finally {
      updateLastAssistant((m) => ({ ...m, streaming: false }));
      setSending(false);
      // Bump active chat to the top of the sidebar list.
      setChats((prev) => {
        if (!prev.length) return prev;
        const idx = prev.findIndex((c) => c.id === activeChatId);
        if (idx < 0) return prev;
        const updated = { ...prev[idx], updated_at: new Date().toISOString() };
        return [updated, ...prev.slice(0, idx), ...prev.slice(idx + 1)];
      });
    }
  }

  function onKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  const empty = messages.length === 0 && !loadingMessages;
  const personaLabel = persona.name || "Assistant";

  const sidebarVisible = !isMobile;
  const sidebarWidth = 260;

  function formatChatDate(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const same = (a, b) => a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    if (same(d, now)) {
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  }

  function ChatSidebar() {
    return (
      <div style={{
        width: sidebarWidth, flexShrink: 0,
        borderRight: "1px solid #e5e7eb",
        background: "#fafafa",
        display: "flex", flexDirection: "column",
        height: "100%",
      }}>
        <div style={{
          padding: "16px 16px 8px", display: "flex", alignItems: "center",
          justifyContent: "space-between", flexShrink: 0,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", letterSpacing: 0.5 }}>
            CHATS
          </div>
          <button
            type="button"
            onClick={startNewChat}
            title="New chat"
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "4px 10px", borderRadius: 999,
              background: "#111827", color: "#fff",
              border: "none", fontSize: 12, fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <FaPlus style={{ fontSize: 10 }} /> New
          </button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "4px 8px 16px" }}>
          {chatsLoading && (
            <div style={{ padding: "8px 12px", color: "#9ca3af", fontSize: 12 }}>
              Loading…
            </div>
          )}
          {!chatsLoading && chats.length === 0 && (
            <div style={{ padding: "8px 12px", color: "#9ca3af", fontSize: 12 }}>
              No chats yet
            </div>
          )}
          {chats.map((c) => {
            const active = c.id === activeChatId;
            return (
              <div
                key={c.id}
                onClick={() => openChat(c.id)}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "8px 10px", marginBottom: 2,
                  borderRadius: 8, cursor: "pointer",
                  background: active ? "#e5e7eb" : "transparent",
                }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "#f1f3f5"; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, color: "#111827", fontWeight: active ? 600 : 500,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>{c.title}</div>
                  <div style={{ fontSize: 10, color: "#9ca3af" }}>
                    {formatChatDate(c.updated_at)}
                  </div>
                </div>
                <button
                  type="button"
                  aria-label="Delete chat"
                  onClick={(e) => deleteChat(c.id, e)}
                  style={{
                    background: "transparent", border: "none",
                    color: "#9ca3af", cursor: "pointer",
                    padding: 4, fontSize: 11, flexShrink: 0,
                  }}
                  title="Delete chat"
                >
                  <FaTrash />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function MobileChatPicker() {
    const active = chats.find((c) => c.id === activeChatId);
    const label = active ? active.title : "New chat";
    return (
      <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
        <button
          type="button"
          onClick={() => setMobileChatPickerOpen((v) => !v)}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "transparent", border: "none",
            padding: "4px 0", cursor: "pointer",
            maxWidth: "100%",
          }}
        >
          <span style={{
            fontSize: 14, fontWeight: 600, color: "#111827",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            maxWidth: 180,
          }}>{label}</span>
          <FaChevronDown style={{ fontSize: 10, color: "#6b7280" }} />
        </button>
        {mobileChatPickerOpen && (
          <>
            <div
              onClick={() => setMobileChatPickerOpen(false)}
              style={{
                position: "fixed", inset: 0, background: "rgba(0,0,0,0.2)",
                zIndex: 40,
              }}
            />
            <div style={{
              position: "absolute", top: "100%", left: 0,
              marginTop: 6, width: 260, maxHeight: 360,
              overflowY: "auto", background: "#fff",
              border: "1px solid #e5e7eb", borderRadius: 10,
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 50,
              padding: 6,
            }}>
              <button
                type="button"
                onClick={startNewChat}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  width: "100%", padding: "8px 10px", borderRadius: 8,
                  background: "#111827", color: "#fff", border: "none",
                  fontSize: 13, fontWeight: 600, cursor: "pointer",
                  marginBottom: 6,
                }}
              >
                <FaPlus style={{ fontSize: 11 }} /> New chat
              </button>
              {chats.length === 0 && (
                <div style={{ padding: "8px 10px", color: "#9ca3af", fontSize: 12 }}>
                  No chats yet
                </div>
              )}
              {chats.map((c) => {
                const isActive = c.id === activeChatId;
                return (
                  <div
                    key={c.id}
                    onClick={() => openChat(c.id)}
                    style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "8px 10px", borderRadius: 8, cursor: "pointer",
                      background: isActive ? "#e5e7eb" : "transparent",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 13, color: "#111827",
                        fontWeight: isActive ? 600 : 500,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>{c.title}</div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => deleteChat(c.id, e)}
                      style={{
                        background: "transparent", border: "none",
                        color: "#9ca3af", cursor: "pointer", padding: 4, fontSize: 11,
                      }}
                      title="Delete chat"
                    ><FaTrash /></button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div style={{
      flex: 1, minWidth: 0, display: "flex", flexDirection: "row",
      background: "#fff", boxSizing: "border-box", width: "100%",
      paddingTop: isMobile ? 56 : 0,
      height: "100vh", overflow: "hidden",
    }}>
      <style>{`
        @keyframes ai-spin { to { transform: rotate(360deg); } }
        @keyframes ai-blink { 50% { opacity: 0; } }
        @keyframes ai-skel {
          0% { opacity: 0.55; }
          50% { opacity: 1; }
          100% { opacity: 0.55; }
        }
      `}</style>

      {sidebarVisible && !personaLoading && <ChatSidebar />}

      <div style={{
        flex: 1, minWidth: 0, display: "flex", flexDirection: "column",
        height: "100%",
      }}>
      {personaLoading ? (
        <>
          <HeaderSkeleton isMobile={isMobile} />
          <BodySkeleton isMobile={isMobile} />
        </>
      ) : (
        <>
          {/* Header */}
          <div style={{
            display: "flex", alignItems: "center", gap: 12,
            padding: isMobile ? "12px 16px" : "16px 32px",
            borderBottom: "1px solid #e5e7eb", flexShrink: 0,
          }}>
            <PersonaAvatar name={personaLabel} photo={persona.photo} size={isMobile ? 32 : 36} />
            {isMobile ? (
              <MobileChatPicker />
            ) : (
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
                  {personaLabel}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  {persona.title || "Your AI agent"}
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={() => setShowAgentSettingsModal(true)}
              title="Configure your AI assistant"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "6px 12px", borderRadius: 999,
                background: "#f3f4f6", border: "1px solid #e5e7eb",
                color: "#374151", fontSize: 12, fontWeight: 600,
                cursor: "pointer", flexShrink: 0,
              }}
            >
              <FaCog style={{ fontSize: 12 }} />
              {!isMobile && <span>Configure</span>}
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} style={{
            flex: 1, overflowY: "auto",
            padding: isMobile ? "8px 16px 16px" : "8px 32px 16px",
          }}>
            <div style={{ maxWidth: 760, margin: "0 auto" }}>
              {empty && (
                <div style={{ paddingTop: isMobile ? 40 : 80, textAlign: "center" }}>
                  <PersonaAvatar name={personaLabel} photo={persona.photo} size={64} />
                  <h2 style={{
                    fontSize: isMobile ? 22 : 26, fontWeight: 700,
                    color: "#111827", margin: "16px 0 8px",
                  }}>
                    Hi, I'm {personaLabel}
                  </h2>
                  <p style={{ color: "#6b7280", fontSize: 14, margin: "0 auto 24px", maxWidth: 480 }}>
                    Ask me about your contacts and activity, or have me create an article,
                    infographic, or shareable property link for you.
                  </p>
                  <div style={{
                    display: "flex", flexWrap: "wrap", gap: 8,
                    justifyContent: "center", maxWidth: 600, margin: "0 auto",
                  }}>
                    {SUGGESTIONS.map((s) => {
                      const Icon = s.icon;
                      return (
                        <button
                          key={s.label}
                          onClick={() => send(s.label)}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 8,
                            background: "#f6f8fa", border: "1px solid #e5e7eb",
                            color: "#374151", padding: "8px 14px",
                            borderRadius: 999, fontSize: 13, cursor: "pointer",
                          }}
                        >
                          <Icon style={{ fontSize: 12, color: "#0f3c3f" }} />
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {loadingMessages && (
                <div style={{
                  display: "flex", justifyContent: "center",
                  padding: "40px 0", color: "#9ca3af", fontSize: 13,
                }}>
                  <FaSpinner style={{
                    animation: "ai-spin 1s linear infinite", marginRight: 8,
                  }} />
                  Loading conversation…
                </div>
              )}

              {messages.map((m, i) => (
                <MessageBubble key={i} msg={m} persona={persona} />
              ))}
            </div>
          </div>

          {/* Composer */}
          <div style={{
            padding: isMobile ? "10px 12px 16px" : "12px 32px 24px",
            flexShrink: 0, background: "#fff",
          }}>
            {!empty && !sending && (
              <div style={{
                maxWidth: 760, margin: "0 auto 10px",
                display: "flex", gap: 8,
                overflowX: "auto", paddingBottom: 4,
                WebkitOverflowScrolling: "touch",
                scrollbarWidth: "none",
              }}>
                {SUGGESTIONS.map((s) => {
                  const Icon = s.icon;
                  return (
                    <button
                      key={s.label}
                      onClick={() => send(s.label)}
                      title={s.label}
                      style={{
                        flexShrink: 0,
                        display: "inline-flex", alignItems: "center", gap: 6,
                        background: "#f6f8fa", border: "1px solid #e5e7eb",
                        color: "#374151", padding: "6px 12px",
                        borderRadius: 999, fontSize: 12, cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Icon style={{ fontSize: 11, color: "#0f3c3f" }} />
                      {s.label}
                    </button>
                  );
                })}
              </div>
            )}
            <div style={{
              maxWidth: 760, margin: "0 auto",
              display: "flex", alignItems: "flex-end", gap: 8,
              background: "#fff", border: "1px solid #d1d5db",
              borderRadius: 28, padding: "8px 10px 8px 18px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
            }}>
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                placeholder="Ask anything"
                disabled={sending}
                style={{
                  flex: 1, border: "none", outline: "none", resize: "none",
                  fontSize: 15, lineHeight: 1.4, padding: "8px 4px",
                  background: "transparent", fontFamily: "inherit",
                  maxHeight: 160,
                }}
              />
              <button
                type="button"
                aria-label="Send"
                onClick={() => send()}
                disabled={sending || !input.trim()}
                style={{
                  width: 34, height: 34, borderRadius: "50%",
                  background: input.trim() && !sending ? "#111827" : "#e5e7eb",
                  color: input.trim() && !sending ? "#fff" : "#9ca3af",
                  border: "none", cursor: input.trim() && !sending ? "pointer" : "default",
                  flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <FaArrowUp style={{ fontSize: 14 }} />
              </button>
            </div>
            <div style={{
              textAlign: "center", color: "#9ca3af", fontSize: 11,
              marginTop: 8,
            }}>
              {personaLabel} can make mistakes. Verify important info.
            </div>
          </div>
        </>
      )}
      </div>

      {pendingDeleteChatId !== null && (
        <div
          onClick={() => { if (!deletingChat) setPendingDeleteChatId(null); }}
          style={{
            position: "fixed", top: 0, bottom: 0,
            left: isMobile ? 0 : 260, right: 0,
            background: "rgba(17,24,39,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1000, padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 14, width: "100%", maxWidth: 380,
              padding: "20px 22px", boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 600, color: "#111827", marginBottom: 6 }}>
              Delete this chat?
            </div>
            <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 18, lineHeight: 1.45 }}>
              This conversation and its messages will be permanently removed.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                type="button"
                disabled={deletingChat}
                onClick={() => setPendingDeleteChatId(null)}
                style={{
                  padding: "8px 14px", borderRadius: 8,
                  background: "#fff", color: "#374151",
                  border: "1px solid #d1d5db", fontSize: 13,
                  cursor: deletingChat ? "default" : "pointer", fontWeight: 500,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deletingChat}
                onClick={confirmDeleteChat}
                style={{
                  padding: "8px 14px", borderRadius: 8,
                  background: "#dc2626", color: "#fff",
                  border: "1px solid #dc2626", fontSize: 13,
                  cursor: deletingChat ? "default" : "pointer", fontWeight: 600,
                  display: "inline-flex", alignItems: "center", gap: 6,
                }}
              >
                {deletingChat && <FaSpinner style={{ animation: "ai-spin 1s linear infinite", fontSize: 12 }} />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showBetaDisclaimer && (
        <div
          style={{
            position: "fixed", top: 0, bottom: 0,
            left: isMobile ? 0 : 260, right: 0,
            background: "rgba(17,24,39,0.55)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1100, padding: 16,
          }}
        >
          <div
            style={{
              background: "#fff", borderRadius: 14, width: "100%", maxWidth: 440,
              padding: "22px 24px", boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
            }}
          >
            <div style={{
              display: "inline-block", padding: "2px 8px", marginBottom: 10,
              background: "#fef3c7", color: "#92400e", borderRadius: 999,
              fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase",
            }}>
              Beta
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#111827", marginBottom: 8 }}>
              This feature is under active development
            </div>
            <div style={{ fontSize: 13, color: "#4b5563", marginBottom: 14, lineHeight: 1.5 }}>
              Your AI agent is a brand-new feature and is being actively improved.
              You may encounter rough edges, and the AI can make mistakes — please
              double-check anything important before sending it to clients.
            </div>
            <div style={{ fontSize: 13, color: "#4b5563", marginBottom: 18, lineHeight: 1.5 }}>
              You can configure your agent's name, tone, signature and behaviour in{" "}
              <button
                type="button"
                onClick={() => { setShowBetaDisclaimer(false); setShowAgentSettingsModal(true); }}
                style={{
                  background: "none", border: "none", padding: 0,
                  color: "#0f3c3f", fontWeight: 600, textDecoration: "underline",
                  cursor: "pointer", fontSize: 13,
                }}
              >
                AI Agent settings
              </button>.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => setShowBetaDisclaimer(false)}
                style={{
                  padding: "9px 18px", borderRadius: 8,
                  background: "#0f3c3f", color: "#fff",
                  border: "1px solid #0f3c3f", fontSize: 13,
                  cursor: "pointer", fontWeight: 600,
                }}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      <AIAgentSettingsModal
        show={showAgentSettingsModal}
        onHide={() => setShowAgentSettingsModal(false)}
      />
    </div>
  );
}
